import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@refinedev/core';
import {
  Button,
  Flex,
  Loader,
  Select,
  Text,
  TextInput,
  useComputedColorScheme,
} from '@mantine/core';
import * as Sentry from '@sentry/react';
import { IconPrinter } from '@tabler/icons-react';
import { useSendFax } from 'api/action/fax';
import { Fax, FaxStatus, Payor } from 'interfaces';
import { useCurrentSiteStore, useCurrentUserStore } from 'store';
import {
  CUSTOM_FAX_RECIPIENT,
  buildFaxRecipientMap,
  constructFaxAttachmentS3Uri,
  getClaimPayorFaxRecipient,
  normalizeFaxNumber,
} from 'utils/faxUtils';
import { validatePhoneNumber } from 'utils/validate';
import { buttonTextColor } from 'utils/buttonUtils';
import { nowISO } from 'utils/dateUtils';
import { v4 as uuidv4 } from 'uuid';

interface ClaimInvoiceFaxFlowProps {
  attachmentBucket: string;
  attachmentKey: string;
  claimID?: string | null;
  payors: Payor[];
  claimPayorID?: string | null;
  showFaxControls?: boolean;
  disabled?: boolean;
  onBeforeSend?: () => Promise<boolean> | boolean;
  sendButtonLabel?: string;
  hideSendUntilValid?: boolean;
  showSendButton?: boolean;
  showCancelButton?: boolean;
  onRecipientValidityChange?: (isValid: boolean) => void;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const ClaimInvoiceFaxFlow = ({
  attachmentBucket,
  attachmentKey,
  claimID,
  payors = [],
  claimPayorID,
  showFaxControls = true,
  disabled = false,
  onBeforeSend,
  sendButtonLabel,
  hideSendUntilValid = false,
  showSendButton = true,
  showCancelButton = true,
  onRecipientValidityChange,
  onSuccess,
  onCancel,
}: ClaimInvoiceFaxFlowProps) => {
  const { translate } = useTranslation();
  const { id: userID } = useCurrentUserStore();
  const { id: siteID } = useCurrentSiteStore();

  const computedColorScheme = useComputedColorScheme();
  const sendFax = useSendFax();

  // Map of payor id to fax number. This keeps Select option values unique
  // even when multiple payors share the same fax number.
  const faxRecipientByValue = useMemo(
    () => buildFaxRecipientMap(payors),
    [payors]
  );

  const claimPayorFax = useMemo(
    () => getClaimPayorFaxRecipient(payors, claimPayorID),
    [claimPayorID, payors]
  );

  const defaultRecipient = useMemo(
    () => claimPayorFax?.id || CUSTOM_FAX_RECIPIENT,
    [claimPayorFax?.id]
  );

  const [isOpen, setIsOpen] = useState(showFaxControls);
  const [isSendingFax, setIsSendingFax] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState<string | null>(
    defaultRecipient
  );
  const [customFaxNumber, setCustomFaxNumber] = useState('');

  useEffect(() => {
    setSelectedRecipient(defaultRecipient);
    setCustomFaxNumber('');
  }, [defaultRecipient]);

  useEffect(() => {
    setIsOpen(showFaxControls);
  }, [showFaxControls]);

  const recipientValue =
    selectedRecipient === CUSTOM_FAX_RECIPIENT
      ? normalizeFaxNumber(customFaxNumber)
      : faxRecipientByValue.get(selectedRecipient || '') || '';

  const hasValidRecipient = validatePhoneNumber(recipientValue);
  const hasAttachmentContext =
    Boolean(claimID) &&
    Boolean(attachmentBucket?.trim()) &&
    Boolean(attachmentKey?.trim());
  const isActionDisabled = disabled || isSendingFax;
  const isSendDisabled =
    isActionDisabled || !hasValidRecipient || !hasAttachmentContext;
  const shouldShowSendButton =
    showSendButton && (!hideSendUntilValid || hasValidRecipient);

  useEffect(() => {
    onRecipientValidityChange?.(hasValidRecipient);
  }, [hasValidRecipient, onRecipientValidityChange]);

  const faxRecipientOptions = useMemo(
    () => [
      ...payors.map((payor) => ({
        value: payor.id,
        label: `${payor.name} (${payor.fax})`,
      })),
      {
        value: CUSTOM_FAX_RECIPIENT,
        label: translate('pages:claims.claimInvoices.customFaxNumberOption'),
      },
    ],
    [payors, translate]
  );

  const resetAndClose = () => {
    setCustomFaxNumber('');
    setSelectedRecipient(defaultRecipient);
    setIsOpen(showFaxControls);
  };

  const handleCancel = () => {
    resetAndClose();
    onCancel?.();
  };

  const handleSendFax = async (): Promise<boolean> => {
    if (isSendDisabled) {
      return false;
    }

    try {
      setIsSendingFax(true);
      if (onBeforeSend) {
        const canProceed = await onBeforeSend();
        if (!canProceed) {
          return false;
        }
      }

      const faxData: Fax = {
        claimID: claimID as string,
        attachmentKey: constructFaxAttachmentS3Uri(
          attachmentBucket,
          attachmentKey
        ),
        createdBy: userID,
        status: FaxStatus.Pending,
        recipient: recipientValue,
        createdAt: nowISO(),
        id: uuidv4(),
        siteID,
      };

      await sendFax.mutateAsync({ faxData });
      resetAndClose();
      onSuccess?.();
      return true;
    } catch (error) {
      Sentry.setContext('component', { filename: 'ClaimInvoiceFaxFlow.tsx' });
      Sentry.captureException(error);
      return false;
    } finally {
      setIsSendingFax(false);
    }
  };

  if (isSendingFax) {
    return (
      <Flex
        justify="center"
        align="center"
        gap="xs"
        py="xl"
        mih={320}
        data-testid={'fax-sending-loader'}
      >
        <Loader size="sm" />
        <Text size="sm">{translate('pages:claims.claimInvoices.sending')}</Text>
      </Flex>
    );
  }

  if (!isOpen) {
    return (
      <Flex justify="center" gap="sm">
        <Button
          size="xs"
          c={buttonTextColor(computedColorScheme, false)}
          variant="outline"
          w={'20%'}
          onClick={() => setIsOpen(true)}
          disabled={isActionDisabled}
          data-testid={'open-fax-flow'}
        >
          <IconPrinter size={16} style={{ marginRight: '4px' }} />
          {translate('pages:claims.claimInvoices.sendFax')}
        </Button>
      </Flex>
    );
  }

  const isCustomRecipient = selectedRecipient === CUSTOM_FAX_RECIPIENT;

  return (
    <>
      <Flex direction="column" gap="sm">
        <Select
          label={translate('pages:claims.claimInvoices.faxRecipient')}
          placeholder={translate(
            'pages:claims.claimInvoices.selectFaxRecipient'
          )}
          data={faxRecipientOptions}
          value={selectedRecipient}
          allowDeselect={false}
          onChange={(value) => {
            setSelectedRecipient(value);
            if (value !== CUSTOM_FAX_RECIPIENT) {
              setCustomFaxNumber('');
            }
          }}
          data-testid={'fax-recipient-select'}
        />
        {isCustomRecipient && (
          <TextInput
            label={translate('pages:claims.claimInvoices.customFaxNumber')}
            placeholder="+12045550123"
            value={customFaxNumber}
            onChange={(event) => {
              setCustomFaxNumber(event.currentTarget.value);
            }}
            data-testid={'custom-fax-number-input'}
          />
        )}
      </Flex>
      <Flex justify="center" gap="sm" align={'center'}>
        {showCancelButton && (
          <Button
            size="xs"
            c={buttonTextColor(computedColorScheme, false)}
            variant="outline"
            onClick={handleCancel}
            disabled={isActionDisabled}
            data-testid={'cancel-fax-flow'}
          >
            {translate('pages:claims.claimInvoices.cancel')}
          </Button>
        )}
        {shouldShowSendButton && (
          <Button
            size="xs"
            c={buttonTextColor(computedColorScheme, false)}
            variant="outline"
            onClick={handleSendFax}
            disabled={isSendDisabled}
            data-testid={'send-fax'}
          >
            <IconPrinter size={16} style={{ marginRight: '4px' }} />
            {sendButtonLabel || translate('pages:claims.claimInvoices.sendFax')}
          </Button>
        )}
      </Flex>
    </>
  );
};
